import axios from 'axios'

// 根据环境动态设置 API 基础 URL
const getApiBaseUrl = () => {
    // 优先使用环境变量中的配置
    if (process.env.VUE_APP_API_BASE_URL) {
        return process.env.VUE_APP_API_BASE_URL
    }
    // 备用方案
    if (process.env.NODE_ENV === 'development') {
        return '/api'
    }
    return 'https://api.486886.shop/api'
}

const API_BASE_URL = getApiBaseUrl()

// 创建主API实例（用于需要认证的请求）
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000
})

// 创建认证专用API实例（不自动添加token）
const authApi = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000
})

// 主API实例的请求拦截器 - 添加token
api.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// 主API实例的响应拦截器 - 处理错误
api.interceptors.response.use(
    response => {
        return response.data
    },
    error => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('token')
            localStorage.removeItem('userInfo')
            window.location.href = '/login'
        }

        // 添加更详细的错误信息
        if (error.response) {
            // 服务器返回了错误状态码
            const errorMessage = error.response.data?.message ||
                error.response.data?.error ||
                `请求失败: ${error.response.status} ${error.response.statusText}`
            console.error('API错误:', {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
                url: error.config?.url
            })
            error.message = errorMessage
        } else if (error.request) {
            // 请求已发出但没有收到响应
            console.error('网络错误:', error.request)
            error.message = '网络连接失败，请检查网络连接'
        } else {
            // 其他错误
            console.error('请求配置错误:', error.message)
        }

        return Promise.reject(error)
    }
)

// 认证专用API实例的响应拦截器
authApi.interceptors.response.use(
    response => {
        return response.data
    },
    error => {
        // 添加更详细的错误信息
        if (error.response) {
            const errorMessage = error.response.data?.message ||
                error.response.data?.error ||
                `请求失败: ${error.response.status} ${error.response.statusText}`
            console.error('认证API错误:', {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
                url: error.config?.url
            })
            error.message = errorMessage
        } else if (error.request) {
            console.error('网络错误:', error.request)
            error.message = '网络连接失败，请检查网络连接'
        } else {
            console.error('请求配置错误:', error.message)
        }

        return Promise.reject(error)
    }
)

// 认证相关API - 使用不自动添加token的实例
export const authAPI = {
    login: (data) => authApi.post('/auth/login', data),
    register: (data) => authApi.post('/auth/register', data),
    testPhoneLogin: (data) => authApi.post('/auth/test-phone-login', data)
}

// 排班相关API
export const scheduleAPI = {
    getSchedule: (params) => api.get('/schedule', { params }),
    saveSchedule: (data) => api.post('/schedule', data),
    getMonthlySchedule: (params) => api.get('/schedule/monthly', { params }),
    deleteSchedule: (params) => api.delete('/schedule', { params }),
    searchSchedules: (params) => api.get('/schedule/search', { params }),
    // 新增：获取所有排班
    getAllSchedules: () => api.get('/schedule/all'),
    // 新增：根据ID删除排班
    deleteScheduleById: (id) => api.delete(`/schedule/${id}`)
}

// 用户相关API
export const userAPI = {
    getUsers: (params) => api.get('/users', { params }),
    createUser: (data) => api.post('/users', data),
    updateUser: (data) => api.put('/users', data),
    toggleUserStatus: (id) => api.patch(`/users/${id}/status`),
    resetPassword: (id) => api.post(`/users/${id}/reset-password`),
    deleteUser: (id) => api.delete(`/users/${id}`),
    // 个人中心相关API
    getProfile: () => api.get('/user/profile'), // 新增：获取个人信息
    updateProfile: (data) => api.put('/user/profile', data),
    changePassword: (data) => api.post('/user/change-password', data),
    saveNotificationSettings: (data) => api.post('/user/notification-settings', data),
    uploadAvatar: (data) => api.post('/user/upload-avatar', data)
}

// 预约相关API
export const reservationAPI = {
    create: (data) => api.post('/reservations', data),
    getMyReservations: () => api.get('/reservations/my'),
    cancel: (id) => api.post(`/reservations/${id}/cancel`),
    delete: (id) => api.delete(`/reservations/${id}`),
    // 获取已被预约的时间段
    getReservedSlots: (params) => api.get('/reservations/reserved-slots', { params }),
    // 获取时间段状态信息（包含人数限制）
    getTimeSlotStatus: (params) => api.get('/reservations/time-slot-status', { params })
}

// 教练相关API
export const coachAPI = {
    getCoaches: () => api.get('/coaches'),
    getAllCoaches: () => api.get('/coaches/all'),
    createCoach: (data) => api.post('/coaches', data),
    addCoachByAdmin: (data) => api.post('/coaches/admin/add', data),
    updateCoach: (data) => api.put('/coaches', data),
    getStudents: () => api.get('/coaches/students'),
    getReservations: (params) => api.get('/coaches/reservations', { params }),
    confirmReservation: (id) => api.post(`/coaches/reservations/${id}/confirm`),
    rejectReservation: (id) => api.post(`/coaches/reservations/${id}/reject`),
    completeReservation: (id) => api.post(`/coaches/reservations/${id}/complete`),
    addStudentRemark: (data) => api.post('/coaches/students/remark', data),
    getAvailableSlots: (params) => api.get('/coaches/available-slots', { params }),
    getCoachSchedule: (params) => api.get('/coaches/schedule', { params }),
}

// 学员相关API
export const studentAPI = {
    getProgress: () => api.get('/students/progress')
}

// 系统设置API
export const systemAPI = {
    // 获取所有系统设置
    getSettings: () => api.get('/system/settings'),
    // 保存预约设置
    saveAppointmentSettings: (data) => api.post('/system/appointment-settings', data),
    // 保存学时设置
    saveHoursSettings: (data) => api.post('/system/hours-settings', data),
    // 保存通知设置
    saveNotificationSettings: (data) => api.post('/system/notification-settings', data)
}

export default api
